import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bell,
  ClipboardList,
  ClipboardCheck,
  MessageSquare,
  CheckCircle,
  CalendarClock,
  Video,
} from 'lucide-react';
import { supabase } from '../services/supabase';
import { listaEnCastellano } from '../lib/leerVarias';
import { diaDe } from '../lib/fechaLocal';

// Panel ENTRENADOR del Dashboard admin: agrega las tareas pendientes de TODOS
// los entrenadores del club (el admin/director las ve indicando de quién son;
// el propio entrenador las ve en su dashboard). Conceptos por monitor:
//  - Evaluaciones mensuales pendientes  → RPC formal_evaluations_pending_for_trainer.
//  - Sesiones sin pasar lista           → sesiones pasadas de sus grupos con
//    enrollments confirmados y attendance IS NULL.
//  - Mensajes sin leer                  → messages dirigidos al monitor con read=false.
//  - PF sin planificar                  → RPC gym_players_unplanned_48h (role-aware;
//    admin recibe todos con monitor_id/monitor_name → se agrupa por entrenador).
//  - Agendar vídeo-análisis             → bookings con trainer_id = monitor,
//    status='pending' (sin fecha) → el entrenador debe acordar y agendar.
// Es informativo (el admin no agenda por el monitor); cada fila resume la carga.
// Excepción: el badge "vídeo sin agendar" enlaza a /video-analisis (el admin puede
// entrar a la pantalla de Análisis de vídeo desde ahí).
//
// POR QUÉ EL «TODO AL DÍA» TIENE CONDICIONES
// ------------------------------------------
// El `try/catch` que envolvía las ocho lecturas era código muerto: postgrest-js
// no rechaza nunca (devuelve `{ data: null, error }` mientras
// `shouldThrowOnError` sea false), así que `setError(true)` no se ejecutaba
// jamás. Y había una puerta peor que las demás en la lectura 1: si `profiles`
// fallaba, `monitors.length` era 0, el `return` temprano dejaba el estado en
// EMPTY y el panel pintaba los cinco totales a 0 y «Todo al día — sin tareas
// pendientes». Un club entero de evaluaciones y listas sin pasar, resumido en
// una frase verde.
//
// Ahora: si no hay lista de monitores no hay panel que construir y se dice; y
// cada total es `number | null`, donde null = SIN COMPROBAR, se pinta «—» y
// bloquea el «Todo al día».

const QUE_MONITORES = 'los entrenadores';
const QUE_ASISTENCIAS = 'las listas sin pasar';
const QUE_MENSAJES = 'los mensajes sin leer';
const QUE_EVALS = 'las evaluaciones pendientes';
const QUE_PF = 'la preparación física sin planificar';
const QUE_VIDEO = 'los vídeo-análisis sin agendar';

/** null = no se pudo leer. No es 0: un 0 aquí significa «nada pendiente». */
type Cuenta = number | null;

interface MonitorAvisos {
  id: string;
  name: string;
  evals: number;
  asistencias: number;
  mensajes: number;
  pfUnplanned: number;
  videoToSchedule: number;
}

interface AvisosState {
  monitors: MonitorAvisos[];
  totalEvals: Cuenta;
  totalAsistencias: Cuenta;
  totalMensajes: Cuenta;
  totalPfUnplanned: Cuenta;
  totalVideoToSchedule: Cuenta;
}

const EMPTY: AvisosState = {
  monitors: [],
  totalEvals: 0,
  totalAsistencias: 0,
  totalMensajes: 0,
  totalPfUnplanned: 0,
  totalVideoToSchedule: 0,
};

export default function AvisosPanel() {
  const [state, setState] = useState<AvisosState>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [errorCarga, setErrorCarga] = useState<string | null>(null);
  const [fallidos, setFallidos] = useState<string[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorCarga(null);
    const noLeidos: string[] = [];
    const sinComprobar = (que: string): null => {
      noLeidos.push(que);
      return null;
    };

    const now = new Date();
    const today = diaDe(now);
    const from = new Date(now);
    from.setDate(from.getDate() - 60);
    const fromStr = diaDe(from);

    // 1) Monitores activos del club. Es la lectura de la que cuelgan todas las
    //    demás: sin ella no hay filas ni a quién atribuirlas, así que su fallo
    //    NO es un total a «—», es el panel entero sin poder construirse.
    const { data: monitorsData, error: errorMonitors } = await supabase
      .from('profiles')
      .select('id, full_name')
      .eq('role', 'monitor')
      .eq('active', true)
      .order('full_name');
    if (errorMonitors) {
      setErrorCarga(errorMonitors.message || 'Error desconocido');
      setFallidos([QUE_MONITORES]);
      setState(EMPTY);
      setLoading(false);
      return;
    }
    const monitors = (monitorsData ?? []) as { id: string; full_name: string }[];
    if (!monitors.length) {
      // Leído de verdad y sin monitores: el club no tiene entrenadores activos.
      setState(EMPTY);
      setFallidos([]);
      setLoading(false);
      return;
    }
    const monitorIds = monitors.map((m) => m.id);

    // 2-4) Asistencias pendientes: grupos → sesiones pasadas (60d) →
    //      enrollments confirmados sin attendance, contados por monitor. Son
    //      tres lecturas encadenadas para una sola cifra: si falla cualquiera,
    //      la cifra no existe (sin los grupos no hay sesiones que mirar).
    const asistenciasByMonitor: Record<string, number> = {};
    const contarAsistencias = async (): Promise<Cuenta> => {
      const { data: groupsData, error: errorGroups } = await supabase
        .from('groups')
        .select('id, monitor_id')
        .eq('active', true)
        .in('monitor_id', monitorIds);
      if (errorGroups) return sinComprobar(QUE_ASISTENCIAS);
      const groupToMonitor: Record<string, string> = {};
      for (const g of (groupsData ?? []) as { id: string; monitor_id: string | null }[]) {
        if (g.monitor_id) groupToMonitor[g.id] = g.monitor_id;
      }
      const groupIds = Object.keys(groupToMonitor);
      if (!groupIds.length) return 0;

      const { data: pastSessions, error: errorSessions } = await supabase
        .from('sessions')
        .select('id, group_id')
        .in('group_id', groupIds)
        .gte('date', fromStr)
        .lt('date', today);
      if (errorSessions) return sinComprobar(QUE_ASISTENCIAS);
      const sessionToMonitor: Record<string, string> = {};
      for (const s of (pastSessions ?? []) as { id: string; group_id: string | null }[]) {
        const mon = s.group_id ? groupToMonitor[s.group_id] : undefined;
        if (mon) sessionToMonitor[s.id] = mon;
      }
      const pastSessionIds = Object.keys(sessionToMonitor);
      if (!pastSessionIds.length) return 0;

      const { data: enrolls, error: errorEnrolls } = await supabase
        .from('enrollments')
        .select('session_id, attendance')
        .in('session_id', pastSessionIds)
        .eq('status', 'confirmed')
        .is('attendance', null);
      if (errorEnrolls) return sinComprobar(QUE_ASISTENCIAS);
      let total = 0;
      for (const e of (enrolls ?? []) as { session_id: string; attendance: boolean | null }[]) {
        const mon = sessionToMonitor[e.session_id];
        if (mon) {
          asistenciasByMonitor[mon] = (asistenciasByMonitor[mon] ?? 0) + 1;
          total += 1;
        }
      }
      return total;
    };

    // 5) Mensajes sin leer dirigidos a cada monitor.
    const mensajesByMonitor: Record<string, number> = {};
    const contarMensajes = async (): Promise<Cuenta> => {
      // `read` aquí es LO CORRECTO, y desde la mig 324 además es preciso: lo que
      // se pregunta es si el DESTINATARIO lo ha visto, no si lo he visto yo. El
      // trigger de la 324 solo lo deriva cuando quien registra la lectura es el
      // destinatario, así que este booleano responde exactamente a eso. Lo que
      // pasó a `message_reads` son los sitios que afirman «no lo has leído TÚ».
      const { data: unread, error } = await supabase
        .from('messages')
        .select('receiver_id')
        .eq('read', false)
        .in('receiver_id', monitorIds);
      if (error) return sinComprobar(QUE_MENSAJES);
      let total = 0;
      for (const m of (unread ?? []) as { receiver_id: string | null }[]) {
        if (m.receiver_id) {
          mensajesByMonitor[m.receiver_id] = (mensajesByMonitor[m.receiver_id] ?? 0) + 1;
          total += 1;
        }
      }
      return total;
    };

    // 6) Evaluaciones formales pendientes por entrenador (PR-EV1): alumnos
    //    con ≥8 sesiones asistidas desde su última evaluación. Una RPC por
    //    monitor; el `try/catch` de cada una era código muerto y el `count: 0`
    //    del catch decía «este entrenador no tiene evaluaciones pendientes».
    //    Si falla la de UN monitor el total ya no es el total: «—».
    const evalsByMonitor: Record<string, number> = {};
    const contarEvals = async (): Promise<Cuenta> => {
      const evalResults = await Promise.all(
        monitorIds.map(async (id) => {
          const { data, error } = await supabase.rpc('formal_evaluations_pending_for_trainer', {
            p_trainer_id: id,
          });
          return { id, count: Array.isArray(data) ? data.length : 0, error };
        })
      );
      if (evalResults.some((r) => r.error)) return sinComprobar(QUE_EVALS);
      let total = 0;
      for (const r of evalResults) {
        evalsByMonitor[r.id] = r.count;
        total += r.count;
      }
      return total;
    };

    // 7) PF sin planificar 48h por entrenador. La RPC es role-aware: para
    //    admin devuelve TODOS los jugadores GYM activos sin rutina publicada
    //    en hoy..hoy+2, con monitor_id/monitor_name → agrupamos por monitor
    //    (las filas con monitor NULL = sin PF asignado; eso lo cubre el panel
    //    Director "Bonos Prep. Fís. sin entrenador", aquí se ignoran).
    const pfByMonitor: Record<string, number> = {};
    const contarPf = async (): Promise<Cuenta> => {
      const { data: unplannedRaw, error } = await supabase.rpc('gym_players_unplanned_48h');
      if (error) return sinComprobar(QUE_PF);
      let total = 0;
      for (const r of (unplannedRaw ?? []) as { monitor_id: string | null }[]) {
        if (r.monitor_id) {
          pfByMonitor[r.monitor_id] = (pfByMonitor[r.monitor_id] ?? 0) + 1;
          total += 1;
        }
      }
      return total;
    };

    // 8) Agendar vídeo-análisis por entrenador: bookings con trainer_id
    //    asignado y status='pending' (sin fecha) → el entrenador debe agendar.
    const videoByMonitor: Record<string, number> = {};
    const contarVideo = async (): Promise<Cuenta> => {
      const { data: vabRaw, error } = await supabase
        .from('video_analysis_bookings')
        .select('trainer_id')
        .eq('status', 'pending')
        .not('trainer_id', 'is', null);
      if (error) return sinComprobar(QUE_VIDEO);
      let total = 0;
      for (const b of (vabRaw ?? []) as { trainer_id: string | null }[]) {
        if (b.trainer_id) {
          videoByMonitor[b.trainer_id] = (videoByMonitor[b.trainer_id] ?? 0) + 1;
          total += 1;
        }
      }
      return total;
    };

    const [totalAsistencias, totalMensajes, totalEvals, totalPfUnplanned, totalVideoToSchedule] =
      await Promise.all([
        contarAsistencias(),
        contarMensajes(),
        contarEvals(),
        contarPf(),
        contarVideo(),
      ]);

    // El desglose solo puede listar lo que se ha podido contar. Con un concepto
    // sin comprobar, un entrenador que solo tuviera trabajo en ESE concepto no
    // aparece: por eso el aviso de arriba nombra lo que falta.
    const rows: MonitorAvisos[] = monitors
      .map((m) => ({
        id: m.id,
        name: m.full_name,
        evals: evalsByMonitor[m.id] ?? 0,
        asistencias: asistenciasByMonitor[m.id] ?? 0,
        mensajes: mensajesByMonitor[m.id] ?? 0,
        pfUnplanned: pfByMonitor[m.id] ?? 0,
        videoToSchedule: videoByMonitor[m.id] ?? 0,
      }))
      .filter(
        (m) =>
          m.evals > 0 ||
          m.asistencias > 0 ||
          m.mensajes > 0 ||
          m.pfUnplanned > 0 ||
          m.videoToSchedule > 0
      )
      .sort(
        (a, b) =>
          b.evals +
          b.asistencias +
          b.mensajes +
          b.pfUnplanned +
          b.videoToSchedule -
          (a.evals + a.asistencias + a.mensajes + a.pfUnplanned + a.videoToSchedule)
      );

    setState({
      monitors: rows,
      totalEvals,
      totalAsistencias,
      totalMensajes,
      totalPfUnplanned,
      totalVideoToSchedule,
    });
    // El orden de `noLeidos` depende de cuál conteo termine antes, y la frase
    // la lee una persona: se ordena para que no baile entre recargas.
    setFallidos([...new Set(noLeidos)].sort());
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const todoComprobado = fallidos.length === 0;

  return (
    <div className="card" data-testid="avisos-panel">
      <div className="flex items-center gap-2 mb-4">
        <Bell size={18} className="text-gold-primary" />
        <h3 className="font-oswald text-white text-lg uppercase tracking-wide">
          Tareas pendientes · Entrenadores
        </h3>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-text-secondary text-sm font-inter py-4">
          <div className="w-3 h-3 border border-gold-primary/40 border-t-gold-primary rounded-full animate-spin" />
          Cargando avisos…
        </div>
      ) : errorCarga ? (
        <div data-testid="avisos-ilegible" className="py-2">
          <p className="text-error text-sm font-inter">{errorCarga}</p>
          <p className="text-text-secondary text-sm font-inter mt-1">
            Sin la lista de entrenadores no hay nada que resumir. No lo lea como «todo al día».
          </p>
          <button className="btn-secondary mt-3" onClick={load} data-testid="avisos-reintentar">
            Reintentar
          </button>
        </div>
      ) : (
        <>
          {fallidos.length > 0 && (
            <div
              className="text-error text-sm font-inter border border-error/30 rounded px-3 py-2 mb-3 flex items-center justify-between gap-3"
              data-testid="avisos-fallidos"
            >
              <span>
                No se ha podido leer {listaEnCastellano(fallidos)}. Los totales «—» están sin
                comprobar y el desglose de abajo solo lleva lo que sí se ha contado.
              </span>
              <button
                className="btn-secondary shrink-0"
                onClick={load}
                data-testid="avisos-fallidos-reintentar"
              >
                Reintentar
              </button>
            </div>
          )}

          {/* Totales del club */}
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mb-4">
            <TotalChip
              icon={ClipboardList}
              color="#9C27B0"
              value={state.totalEvals}
              label="Evaluaciones"
            />
            <TotalChip
              icon={ClipboardCheck}
              color="#FF9800"
              value={state.totalAsistencias}
              label="Sin pasar lista"
            />
            <TotalChip
              icon={MessageSquare}
              color="#2196F3"
              value={state.totalMensajes}
              label="Mensajes sin leer"
            />
            <TotalChip
              icon={CalendarClock}
              color="#EF4444"
              value={state.totalPfUnplanned}
              label="PF sin planificar"
            />
            <TotalChip
              icon={Video}
              color="#22D3EE"
              value={state.totalVideoToSchedule}
              label="Vídeo sin agendar"
            />
          </div>

          {/* Desglose por entrenador */}
          {state.monitors.length === 0 ? (
            todoComprobado ? (
              <div className="flex items-center gap-2 text-text-secondary text-sm font-inter py-2">
                <CheckCircle size={16} className="text-[#4CAF50]" />
                Todo al día — sin tareas pendientes.
              </div>
            ) : (
              <p
                className="text-text-secondary text-sm font-inter py-2"
                data-testid="avisos-desglose-incompleto"
              >
                Sin desglose: de lo que se ha podido contar, no hay nada pendiente.
              </p>
            )
          ) : (
            <div className="divide-y divide-bg-border">
              {state.monitors.map((m) => (
                <div key={m.id} className="flex items-center justify-between py-2.5">
                  <span className="font-inter text-white text-sm">{m.name}</span>
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    {m.evals > 0 && (
                      <Badge color="#9C27B0" icon={ClipboardList} text={`${m.evals} eval.`} />
                    )}
                    {m.asistencias > 0 && (
                      <Badge
                        color="#FF9800"
                        icon={ClipboardCheck}
                        text={`${m.asistencias} asist.`}
                      />
                    )}
                    {m.mensajes > 0 && (
                      <Badge color="#2196F3" icon={MessageSquare} text={`${m.mensajes} msg.`} />
                    )}
                    {m.pfUnplanned > 0 && (
                      <Badge
                        color="#EF4444"
                        icon={CalendarClock}
                        text={`${m.pfUnplanned} PF sin plan.`}
                      />
                    )}
                    {m.videoToSchedule > 0 && (
                      <Link
                        to="/video-analisis"
                        data-testid={`avisos-video-agendar-${m.id}`}
                        className="rounded-lg hover:opacity-80 transition-opacity focus:outline-none focus:ring-1 focus:ring-[#22D3EE]"
                        title="Ir a Análisis de vídeo para agendar"
                      >
                        <Badge
                          color="#22D3EE"
                          icon={Video}
                          text={`${m.videoToSchedule} vídeo sin agendar`}
                        />
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TotalChip({
  icon: Icon,
  color,
  value,
  label,
}: {
  icon: typeof Bell;
  color: string;
  value: Cuenta;
  label: string;
}) {
  return (
    <div className="bg-bg-elevated border border-bg-border rounded-lg p-3">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={16} style={{ color }} />
        <span className="text-text-secondary font-inter text-xs">{label}</span>
      </div>
      <p
        className={`font-oswald text-2xl leading-none ${value === null ? 'text-error' : 'text-white'}`}
        title={value === null ? 'Sin comprobar: la consulta ha fallado' : undefined}
      >
        {value === null ? '—' : value}
      </p>
    </div>
  );
}

function Badge({ color, icon: Icon, text }: { color: string; icon: typeof Bell; text: string }) {
  return (
    <span
      className="flex items-center gap-1.5 text-xs font-inter rounded-lg px-2.5 py-1 border"
      style={{ color, backgroundColor: `${color}1a`, borderColor: `${color}4d` }}
    >
      <Icon size={12} /> {text}
    </span>
  );
}
